// components/tabs/AttendanceTab.jsx
import React from 'react';
import { Save, Plus, Trash2, Check, X } from 'lucide-react';

export default function AttendanceTab({
    attendanceRules,
    attendanceCodes,
    handleRuleChange,
    handleCodeChange,
    handleAddCode,
    handleDeleteCode,
    handleSave,
    isSaving
}) {
    return (
        <div className="bg-white rounded-xl border border-slate-200/60 shadow-xs p-6 space-y-8 transition-all duration-300 font-dynamic-title">
            
            {/* Section Header */}
            <div>
                <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Attendance Compliance Matrix</h2>
                <p className="text-xs text-slate-400 mt-0.5">Define system status metrics codes and validate automated threshold policy configurations.</p>
            </div>
            <hr className="border-slate-100" />

            {/* SUB-SECTION 1: Attendance Rules Mapping */}
            <div className="space-y-4">
                <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">1. Global Policy Rules</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5 md:col-span-1">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Rule Schema Template Name *</label>
                        <input 
                            type="text" 
                            name="name"
                            value={attendanceRules.name || ''}
                            onChange={handleRuleChange}
                            placeholder="e.g. Standard High School Policy"
                            className="w-full text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:bg-white focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-hidden"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Max Allowed Absences / Term</label>
                        <input 
                            type="number" 
                            name="max_absences"
                            value={attendanceRules.max_absences ?? 0}
                            onChange={handleRuleChange}
                            min="0"
                            className="w-full text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:bg-white focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-hidden"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Max Allowed Tardies / Term</label>
                        <input 
                            type="number" 
                            name="max_tardies"
                            value={attendanceRules.max_tardies ?? 0}
                            onChange={handleRuleChange}
                            min="0"
                            className="w-full text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:bg-white focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-hidden"
                        />
                    </div>
                </div>

                {/* Notifications Toggle */}
                <div className="flex items-start justify-between p-4 rounded-xl bg-slate-50/50 border border-slate-100 mt-2">
                    <div className="space-y-0.5 max-w-[80%]">
                        <label className="text-xs font-bold text-slate-700 block">Enable Compliance Notifications</label>
                        <span className="text-[11px] text-slate-400 block">Dispatches warning alerts instantly when individual target entities breach maximum metrics.</span>
                    </div>
                    <input 
                        type="checkbox" 
                        name="notification_enabled"
                        checked={attendanceRules.notification_enabled ?? true}
                        onChange={(e) => handleRuleChange({ target: { name: 'notification_enabled', value: e.target.checked } })}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 mt-1 cursor-pointer"
                    />
                </div>
            </div>

            <hr className="border-slate-100" />

            {/* SUB-SECTION 2: Dynamic Attendance Codes Setup Table */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">2. Core System Status Matrix Codes</h3>
                        <p className="text-[10px] text-slate-400 mt-0.5">Map unique system keys to discrete Boolean behavior logic fields.</p>
                    </div>
                    <button
                        type="button"
                        onClick={handleAddCode}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold uppercase tracking-wider rounded-lg transition-all active:scale-98 cursor-pointer"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Add Code
                    </button>
                </div>

                {/* Table Layout */}
                <div className="border border-slate-200/60 rounded-xl overflow-hidden bg-white shadow-xs">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200/60 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                                <th className="px-4 py-3 w-28">Status Code</th>
                                <th className="px-4 py-3">Description Map</th>
                                <th className="px-3 py-3 text-center w-24">Present</th>
                                <th className="px-3 py-3 text-center w-24">Absent</th>
                                <th className="px-3 py-3 text-center w-24">Tardy</th>
                                <th className="px-4 py-3 text-center w-16">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                            {attendanceCodes.map((item, index) => (
                                <tr key={item.id || index} className="hover:bg-slate-50/40 transition-colors">
                                    {/* Code Input */}
                                    <td className="px-4 py-2.5">
                                        <input 
                                            type="text"
                                            value={item.code}
                                            placeholder="e.g. EXC"
                                            onChange={(e) => handleCodeChange(index, 'code', e.target.value.toUpperCase())}
                                            className="w-full text-xs font-bold text-slate-800 bg-transparent border-0 border-b border-transparent focus:border-blue-500 focus:ring-0 p-0 outline-hidden"
                                        />
                                    </td>
                                    {/* Description Input */}
                                    <td className="px-4 py-2.5">
                                        <input 
                                            type="text"
                                            value={item.description}
                                            placeholder="Excused Medical Absence"
                                            onChange={(e) => handleCodeChange(index, 'description', e.target.value)}
                                            className="w-full bg-transparent border-0 border-b border-transparent focus:border-blue-500 focus:ring-0 p-0 outline-hidden"
                                        />
                                    </td>
                                    {/* Boolean Columns */}
                                    <td className="px-3 py-2.5 text-center">
                                        <input 
                                            type="checkbox"
                                            checked={item.is_present}
                                            onChange={(e) => handleCodeChange(index, 'is_present', e.target.checked)}
                                            className="text-blue-600 border-slate-300 rounded focus:ring-blue-400 cursor-pointer w-3.5 h-3.5"
                                        />
                                    </td>
                                    <td className="px-3 py-2.5 text-center">
                                        <input 
                                            type="checkbox"
                                            checked={item.is_absent}
                                            onChange={(e) => handleCodeChange(index, 'is_absent', e.target.checked)}
                                            className="text-blue-600 border-slate-300 rounded focus:ring-blue-400 cursor-pointer w-3.5 h-3.5"
                                        />
                                    </td>
                                    <td className="px-3 py-2.5 text-center">
                                        <input 
                                            type="checkbox"
                                            checked={item.is_tardy}
                                            onChange={(e) => handleCodeChange(index, 'is_tardy', e.target.checked)}
                                            className="text-blue-600 border-slate-300 rounded focus:ring-blue-400 cursor-pointer w-3.5 h-3.5"
                                        />
                                    </td>
                                    {/* Delete Button */}
                                    <td className="px-4 py-2.5 text-center">
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteCode(index)}
                                            className="p-1 text-slate-400 hover:text-rose-500 rounded-lg transition-colors cursor-pointer"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {attendanceCodes.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="text-center py-6 text-slate-400 text-xs italic bg-slate-50/20">
                                        No status configurations populated. Click "Add Code" to initiate parameters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <hr className="border-slate-100" />

            {/* TAB INLINE ACTION BAR */}
            <div className="flex items-center justify-end pt-2">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 disabled:opacity-60 rounded-xl shadow-xs transition-all active:scale-98 cursor-pointer gap-2"
                >
                    <Save className="w-3.5 h-3.5" />
                    {isSaving ? "Saving Policies..." : "Save Policies"}
                </button>
            </div>
        </div>
    );
}